/**
 * Iter 86 — Reception 5-step Anticoag Screening wizard.
 *
 * Step-by-step UX layered on top of the iter-85 decision engine
 * (`POST /api/anticoag/evaluate` + `POST /api/anticoag/decisions`). Optimised
 * for front-desk staff under time pressure: one question per screen, large
 * fonts, exact "what to say to the patient" script on every step.
 *
 * Steps (matches Section 3 of the framework):
 *   1. Procedure
 *   2. Blood thinners declared?
 *   3. Indication (WHY)
 *   4. Recent stents / strokes / clots / heart procedures (12 mo)
 *   5. Decision output + log
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, CheckCircle2, AlertTriangle, ShieldCheck, MessageCircle, Search, Copy, Stamp, RefreshCw } from 'lucide-react';
import Header from '../components/Header';
import { SECTIONS } from '../lib/sectionTheme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = SECTIONS.anticoag;

const TIER_STYLE = {
  CONTINUE:             { bg: '#16A34A15', fg: '#15803D', label: 'Continue medication',         icon: CheckCircle2 },
  RADIOLOGIST_REVIEW:   { bg: '#D9770615', fg: '#B45309', label: 'Radiologist review required', icon: ShieldCheck },
  MANDATORY_ESCALATION: { bg: '#DC262615', fg: '#B91C1C', label: 'Mandatory escalation',        icon: AlertTriangle },
};

const STEPS = [
  { n: 1, key: 'procedure',  label: 'Procedure' },
  { n: 2, key: 'medication', label: 'Blood thinners' },
  { n: 3, key: 'indication', label: 'Indication' },
  { n: 4, key: 'recent',     label: 'Recent events' },
  { n: 5, key: 'decision',   label: 'Decision' },
];

// Indication → friendly label mirrors the patient-facing question.
const INDICATIONS = [
  { v: 'af',                label: 'Atrial fibrillation' },
  { v: 'stroke_prevention', label: 'Stroke prevention' },
  { v: 'heart_valve',       label: 'Heart valve' },
  { v: 'coronary_stent',    label: 'Coronary stent' },
  { v: 'dvt',               label: 'DVT' },
  { v: 'pe',                label: 'Pulmonary embolism' },
  { v: 'vascular_disease',  label: 'Vascular disease' },
  { v: 'unknown',           label: 'Unknown / patient unsure' },
];

// Step 4 picks a subset of HIGH_RISK_CONDITIONS that map to the "recent
// stents / strokes / blood clots / heart procedures in the last 12 months"
// question. The full condition list is on the main /anticoag tool.
const RECENT_CONDITIONS = [
  'recent_stent_12m',
  'recent_stroke_3m',
  'recent_dvt_pe_3m',
  'dual_antiplatelet',
  'mechanical_valve',
];

export default function AnticoagScreeningPage() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [library, setLibrary] = useState(null);

  // Wizard state — kept flat so a "Start over" button is one setter.
  const [step, setStep]                   = useState(1);
  const [procId, setProcId]               = useState(searchParams.get('procedure') || '');
  const [filter, setFilter]               = useState('');
  const [takesBT, setTakesBT]             = useState(null);   // true | false | null
  const [meds, setMeds]                   = useState([]);
  const [indication, setIndication]       = useState('');
  const [recentNone, setRecentNone]       = useState(null);   // true (none) | false (some) | null
  const [recentConds, setRecentConds]     = useState([]);
  const [egfr, setEgfr]                   = useState('');   // string so blank is valid; parsed on submit

  // Patient context for the audit log.
  const [patientName, setPatientName]     = useState('');
  const [managingDoctor, setManagingDoctor] = useState('');

  const [result, setResult]               = useState(null);
  const [logged, setLogged]               = useState(null);
  const [busy, setBusy]                   = useState(false);

  useEffect(() => {
    axios.get(`${API}/anticoag/library`, { withCredentials: true })
      .then(r => setLibrary(r.data))
      .catch(() => setLibrary({ procedures: [], medications: [], conditions: [] }));
  }, []);

  const proc = library?.procedures?.find(p => p.id === procId);

  // Evaluate on demand when entering step 5.
  useEffect(() => {
    if (step !== 5 || !procId) { setResult(null); return; }
    setBusy(true);
    const body = {
      procedure_id: procId,
      medication_ids: takesBT ? meds : [],
      conditions: recentNone ? [] : recentConds,
      egfr: egfr ? parseFloat(egfr) : null,
    };
    axios.post(`${API}/anticoag/evaluate`, body, { withCredentials: true })
      .then(r => setResult(r.data))
      .catch(() => setResult(null))
      .finally(() => setBusy(false));
  }, [step, procId, takesBT, meds, recentNone, recentConds, egfr]);

  const canAdvance = (() => {
    if (step === 1) return !!procId;
    if (step === 2) return takesBT === false || (takesBT === true && meds.length > 0);
    if (step === 3) return takesBT === false || !!indication;
    if (step === 4) return recentNone === true || (recentNone === false && recentConds.length > 0);
    return false;
  })();

  const next = () => setStep(s => Math.min(5, s + 1));
  const back = () => setStep(s => Math.max(1, s - 1));

  const resetAll = () => {
    setStep(1); setProcId(''); setTakesBT(null); setMeds([]); setIndication('');
    setRecentNone(null); setRecentConds([]); setPatientName(''); setManagingDoctor('');
    setEgfr(''); setResult(null); setLogged(null);
  };

  const logDecision = async () => {
    if (!procId || !result || busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/anticoag/decisions`, {
        procedure_id: procId,
        medication_ids: takesBT ? meds : [],
        conditions: recentNone ? [] : recentConds,
        egfr: egfr ? parseFloat(egfr) : null,
        patient_name: patientName,
        indication,
        managing_doctor: managingDoctor,
        instructions_given: result.patient_script,
      }, { withCredentials: true });
      setLogged(r.data);
    } catch (_e) { /* ignore */ }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="anticoag-screening-page">
        <PageHeader onReset={resetAll} />
        <Disclaimer />
        <Progress step={step} />

        {!library ? (
          <p className="text-sm text-muted-foreground mt-6">Loading library…</p>
        ) : (
          <div className="mt-6">
            {step === 1 && (
              <Step1 library={library} procId={procId} setProcId={setProcId}
                filter={filter} setFilter={setFilter} proc={proc} />
            )}
            {step === 2 && (
              <Step2 library={library} takesBT={takesBT} setTakesBT={setTakesBT}
                meds={meds} setMeds={setMeds}
                egfr={egfr} setEgfr={setEgfr} />
            )}
            {step === 3 && (
              <Step3 takesBT={takesBT} indication={indication} setIndication={setIndication} />
            )}
            {step === 4 && (
              <Step4 library={library} recentNone={recentNone} setRecentNone={setRecentNone}
                recentConds={recentConds} setRecentConds={setRecentConds} />
            )}
            {step === 5 && (
              <Step5 result={result} busy={busy} logged={logged}
                patientName={patientName} setPatientName={setPatientName}
                managingDoctor={managingDoctor} setManagingDoctor={setManagingDoctor}
                onLog={logDecision} onReset={resetAll}
                onOpenFull={() => nav(`/anticoag?tab=tool`)} />
            )}
          </div>
        )}

        {step < 5 && (
          <NavButtons step={step} back={back} next={next} canAdvance={canAdvance} />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────── header / progress / disclaimer ──────────────
function PageHeader({ onReset }) {
  return (
    <div className="mb-3 flex items-baseline justify-between gap-3">
      <div>
        <div className="flex items-baseline gap-3">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight"
              data-testid="anticoag-screening-title">
            Reception screening · Anticoag
          </h1>
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-0.5 rounded-full"
                style={{ backgroundColor: `${C.color}15`, color: C.color }}>
            v1.0 Draft
          </span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">5 quick questions · ~60 seconds at the front desk</p>
      </div>
      <button onClick={onReset}
        data-testid="anticoag-screening-reset"
        className="text-xs font-semibold inline-flex items-center gap-1 px-2 py-1 rounded border border-border hover:bg-muted/30">
        <RefreshCw className="w-3 h-3" /> Start over
      </button>
    </div>
  );
}

function Disclaimer() {
  return (
    <div className="rounded-lg border-2 p-2.5 mb-3 flex items-start gap-2"
         style={{ borderColor: `${C.color}50`, backgroundColor: `${C.color}08` }}
         data-testid="anticoag-screening-disclaimer">
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: C.color }} />
      <p className="text-[11px] text-foreground">
        <strong style={{ color: C.color }}>NEVER</strong> tell a patient to stop their medication.
        Final cessation decisions rest with the procedural radiologist and/or the patient&apos;s treating
        doctor.
      </p>
    </div>
  );
}

function Progress({ step }) {
  return (
    <ol className="flex items-center gap-1" data-testid="anticoag-screening-progress">
      {STEPS.map((s, i) => {
        const done = step > s.n;
        const active = step === s.n;
        return (
          <li key={s.n} className="flex-1 flex items-center gap-1">
            <div className="flex items-center gap-1.5">
              <span className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold"
                    style={done
                      ? { backgroundColor: C.color, color: 'white' }
                      : active
                        ? { backgroundColor: `${C.color}15`, color: C.color, border: `1.5px solid ${C.color}` }
                        : { backgroundColor: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))' }}>
                {done ? '✓' : s.n}
              </span>
              <span className={`text-[10px] font-semibold uppercase tracking-wider hidden sm:inline ${active ? '' : 'text-muted-foreground'}`}
                    style={active ? { color: C.color } : {}}>
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <span className="flex-1 h-px"
                    style={{ backgroundColor: done ? C.color : 'hsl(var(--border))' }} />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function NavButtons({ step, back, next, canAdvance }) {
  return (
    <div className="mt-6 flex items-center justify-between gap-3">
      <button onClick={back} disabled={step === 1}
        data-testid="anticoag-screening-back"
        className="text-sm font-semibold inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-border disabled:opacity-50">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <button onClick={next} disabled={!canAdvance}
        data-testid="anticoag-screening-next"
        className="text-sm font-semibold inline-flex items-center gap-1 px-4 py-2 rounded-lg text-white disabled:opacity-40"
        style={{ backgroundColor: C.color }}>
        Next <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

// ─────────────────────────── individual steps ────────────────────────────
function Step1({ library, procId, setProcId, filter, setFilter, proc }) {
  const procs = useMemo(() => {
    const f = filter.toLowerCase();
    return library.procedures.filter(p => !f || p.label.toLowerCase().includes(f));
  }, [library, filter]);
  return (
    <StepCard
      stepNum={1}
      heading="Which procedure is the patient booked for?"
      script="“What procedure are you here for today?”"
      testid="anticoag-screening-step1">
      <div className="relative mb-2">
        <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
        <input value={filter} onChange={e => setFilter(e.target.value)}
          data-testid="anticoag-screening-proc-filter"
          placeholder="Filter procedures…"
          className="w-full text-sm bg-background border border-border rounded-lg pl-8 pr-3 py-2" />
      </div>
      <select value={procId} onChange={e => setProcId(e.target.value)}
        data-testid="anticoag-screening-proc-select"
        size={10}
        className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2">
        {['low', 'moderate', 'high'].map(r => (
          <optgroup key={r} label={`${r.charAt(0).toUpperCase()}${r.slice(1)} bleeding risk`}>
            {procs.filter(p => p.risk === r).map(p => (
              <option key={p.id} value={p.id}>{p.label}{p.spine ? ' · SPINE' : ''}</option>
            ))}
          </optgroup>
        ))}
      </select>
      {proc && (
        <div className="mt-3 flex items-center gap-2 text-xs">
          <RiskChip risk={proc.risk} />
          {proc.spine && (
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: '#DC262615', color: '#B91C1C' }}>Spine</span>
          )}
        </div>
      )}
    </StepCard>
  );
}

function Step2({ library, takesBT, setTakesBT, meds, setMeds, egfr, setEgfr }) {
  const dabigatranSelected = meds.includes('dabigatran');
  return (
    <StepCard
      stepNum={2}
      heading="Are you taking any blood thinners?"
      script="“Are you taking any blood thinning medications? Some examples include Warfarin, Eliquis, Xarelto, Pradaxa, Clopidogrel, Aspirin, Brilinta, Clexane, or Heparin.”"
      testid="anticoag-screening-step2">
      <div className="flex gap-2 mb-3">
        <BigYN active={takesBT === true}  onClick={() => setTakesBT(true)}  testid="anticoag-screening-bt-yes">Yes</BigYN>
        <BigYN active={takesBT === false} onClick={() => { setTakesBT(false); setMeds([]); }} testid="anticoag-screening-bt-no">No</BigYN>
      </div>
      {takesBT === true && (
        <div data-testid="anticoag-screening-meds">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
            Tick every medication declared
          </p>
          <div className="flex flex-wrap gap-1.5">
            {library.medications.map(m => {
              const active = meds.includes(m.id);
              return (
                <button key={m.id} type="button"
                  onClick={() => setMeds(active ? meds.filter(x => x !== m.id) : [...meds, m.id])}
                  data-testid={`anticoag-screening-med-${m.id}`}
                  className="text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors"
                  style={active
                    ? { backgroundColor: `${C.color}15`, borderColor: C.color, color: C.color }
                    : { borderColor: 'hsl(var(--border))', color: 'hsl(var(--muted-foreground))' }}>
                  {m.name}{m.brand ? ` (${m.brand})` : ''}
                </button>
              );
            })}
          </div>

          {/* eGFR input — always shown when meds selected, mandatory for dabigatran */}
          <div className="mt-4 rounded-lg border p-3"
               style={dabigatranSelected
                 ? { borderColor: '#DC262660', backgroundColor: '#DC262608' }
                 : { borderColor: 'hsl(var(--border))' }}
               data-testid="anticoag-screening-egfr-wrap">
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: dabigatranSelected ? '#B91C1C' : 'hsl(var(--muted-foreground))' }}>
                eGFR {dabigatranSelected && '· REQUIRED for Pradaxa'} (mL/min/1.73m²)
              </span>
              <input type="number" step="1" min="1" max="200"
                value={egfr} onChange={e => setEgfr(e.target.value)}
                data-testid="anticoag-screening-egfr"
                placeholder="e.g. 65"
                className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
            </label>
            <p className="text-[10px] text-muted-foreground mt-1.5">
              Take from the most recent renal function panel (usually within 3 months). Dabigatran hold time branches on this value.
              {dabigatranSelected && <span className="font-semibold" style={{ color: '#B91C1C' }}> Leaving blank will force mandatory radiologist escalation.</span>}
            </p>
          </div>
        </div>
      )}
    </StepCard>
  );
}

function Step3({ takesBT, indication, setIndication }) {
  if (takesBT === false) {
    return (
      <StepCard stepNum={3} heading="No blood thinners — skip indication"
                script="No follow-up needed for this step." testid="anticoag-screening-step3">
        <p className="text-sm text-foreground">
          Patient declared no blood thinners. Click <strong>Next</strong> to continue.
        </p>
      </StepCard>
    );
  }
  return (
    <StepCard
      stepNum={3}
      heading="What is the medication for?"
      script="“What is the medication for? — for example, atrial fibrillation, stroke prevention, a heart valve, a coronary stent, a DVT, a pulmonary embolism, or vascular disease.”"
      testid="anticoag-screening-step3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="anticoag-screening-indications">
        {INDICATIONS.map(i => {
          const active = indication === i.v;
          return (
            <button key={i.v} type="button"
              onClick={() => setIndication(i.v)}
              data-testid={`anticoag-screening-indication-${i.v}`}
              className="text-sm font-semibold py-2 px-3 rounded-lg border transition-colors text-left"
              style={active
                ? { backgroundColor: `${C.color}15`, borderColor: C.color, color: C.color }
                : { borderColor: 'hsl(var(--border))', color: 'hsl(var(--foreground))' }}>
              {i.label}
            </button>
          );
        })}
      </div>
    </StepCard>
  );
}

function Step4({ library, recentNone, setRecentNone, recentConds, setRecentConds }) {
  const cards = library.conditions.filter(c => RECENT_CONDITIONS.includes(c.id));
  return (
    <StepCard
      stepNum={4}
      heading="Any recent stents, strokes, clots, or heart procedures?"
      script="“Have you had any recent stents, strokes, blood clots, or heart procedures in the past 12 months?”"
      testid="anticoag-screening-step4">
      <div className="flex gap-2 mb-3">
        <BigYN active={recentNone === true}  onClick={() => { setRecentNone(true); setRecentConds([]); }}
               testid="anticoag-screening-recent-no">No — none</BigYN>
        <BigYN active={recentNone === false} onClick={() => setRecentNone(false)} testid="anticoag-screening-recent-yes" warn>Yes</BigYN>
      </div>
      {recentNone === false && (
        <div data-testid="anticoag-screening-recent-list">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
            Which apply?
          </p>
          <div className="space-y-1.5">
            {cards.map(c => {
              const active = recentConds.includes(c.id);
              return (
                <label key={c.id} className="flex items-start gap-2 cursor-pointer rounded-lg p-2 border transition-colors"
                       style={active
                         ? { borderColor: '#DC262640', backgroundColor: '#DC262608' }
                         : { borderColor: 'hsl(var(--border))' }}>
                  <input type="checkbox" checked={active}
                    onChange={e => setRecentConds(e.target.checked
                      ? [...recentConds, c.id]
                      : recentConds.filter(x => x !== c.id))}
                    data-testid={`anticoag-screening-cond-${c.id}`}
                    className="w-4 h-4 mt-0.5 rounded border-border" />
                  <span className="text-sm text-foreground">{c.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </StepCard>
  );
}

function Step5({ result, busy, logged, patientName, setPatientName, managingDoctor, setManagingDoctor, onLog, onReset, onOpenFull }) {
  if (busy || !result) {
    return (
      <StepCard stepNum={5} heading="Decision" script="Running the engine…" testid="anticoag-screening-step5">
        <p className="text-sm text-muted-foreground">Calculating…</p>
      </StepCard>
    );
  }
  const T = TIER_STYLE[result.tier];
  const Icon = T.icon;
  return (
    <StepCard
      stepNum={5}
      heading="Decision & patient script"
      script="“[Read the script below to the patient. Do NOT instruct them to stop their medication.]”"
      testid="anticoag-screening-step5">
      <div className="rounded-xl p-4 border-2 flex items-center gap-3"
           style={{ backgroundColor: T.bg, borderColor: T.fg }}
           data-testid={`anticoag-screening-tier-${result.tier}`}>
        <Icon className="w-7 h-7 shrink-0" style={{ color: T.fg }} />
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: T.fg }}>Recommendation</p>
          <p className="text-xl font-bold" style={{ color: T.fg }}>{T.label}</p>
        </div>
      </div>

      <div className="rounded-lg p-3 border mt-3" style={{ borderColor: `${C.color}40`, backgroundColor: `${C.color}06` }}
           data-testid="anticoag-screening-script">
        <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: C.color }}>
          <MessageCircle className="w-3 h-3 inline mr-1" /> Patient script — read to the patient
        </p>
        <p className="text-sm text-foreground italic">{result.patient_script}</p>
        <button onClick={() => navigator.clipboard.writeText(result.patient_script)}
          data-testid="anticoag-screening-copy-script"
          className="mt-2 text-xs font-semibold inline-flex items-center gap-1" style={{ color: C.color }}>
          <Copy className="w-3 h-3" /> Copy script
        </button>
      </div>

      {result.reasons?.length > 0 && (
        <div className="mt-2" data-testid="anticoag-screening-reasons">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Why?</p>
          <ul className="text-xs text-foreground space-y-0.5">
            {result.reasons.map((r, i) => <li key={i}>• {r}</li>)}
          </ul>
        </div>
      )}

      {/* v2.0 — per-drug concrete hold/restart recommendations */}
      {result.medication_recommendations?.length > 0 && (
        <div className="mt-3 rounded-lg border p-3"
             style={{ borderColor: `${C.color}40`, backgroundColor: `${C.color}06` }}
             data-testid="anticoag-screening-med-recs">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: C.color }}>
            Per-drug recommendation ({result.procedure?.category_label})
          </p>
          <div className="space-y-2">
            {result.medication_recommendations.map((m) => (
              <div key={m.id} className="border-l-2 pl-2.5 py-0.5"
                   style={{ borderColor: C.color }}
                   data-testid={`anticoag-screening-rec-${m.id}`}>
                <p className="text-xs font-bold text-foreground">
                  {m.name}{m.brand ? ` (${m.brand})` : ''}
                </p>
                <p className="text-[11px] text-foreground"><strong>Hold:</strong> {m.hold_pre}</p>
                <p className="text-[11px] text-foreground"><strong>Restart:</strong> {m.restart_post}</p>
                {m.renal_note && (
                  <p className="text-[10px] italic text-muted-foreground mt-0.5">{m.renal_note}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {result.radiologist_confirmation_required && (
        <div className="mt-2 rounded-lg border-2 p-2.5 flex items-start gap-2"
             style={{ borderColor: '#B45309', backgroundColor: '#D9770610' }}
             data-testid="anticoag-screening-radiologist-banner">
          <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" style={{ color: '#B45309' }} />
          <p className="text-[11px] text-foreground">
            <strong style={{ color: '#B45309' }}>RADIOLOGIST MUST CONFIRM</strong> — these are policy-derived
            recommendations only. The procedural radiologist has final authority and must sign off before any medication is held.
          </p>
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-border space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Log this screening to the audit trail (optional but recommended)
        </p>
        <div className="grid grid-cols-2 gap-2">
          <input value={patientName} onChange={e => setPatientName(e.target.value)}
            placeholder="Patient name"
            data-testid="anticoag-screening-patient-name"
            className="text-sm bg-background border border-border rounded-lg px-3 py-2" />
          <input value={managingDoctor} onChange={e => setManagingDoctor(e.target.value)}
            placeholder="Managing doctor (GP / cardiologist)"
            data-testid="anticoag-screening-mgmt-doctor"
            className="text-sm bg-background border border-border rounded-lg px-3 py-2" />
        </div>
        <button onClick={onLog} disabled={!!logged}
          data-testid="anticoag-screening-log-btn"
          className="w-full text-sm font-semibold py-2 rounded-lg text-white disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
          style={{ backgroundColor: C.color }}>
          <Stamp className="w-4 h-4" /> {logged ? '✓ Logged to audit trail' : 'Log screening'}
        </button>
        {logged && (
          <p className="text-[11px] text-muted-foreground text-center" data-testid="anticoag-screening-logged-confirm">
            Decision <code>{logged.decision_id}</code> · tier <strong>{logged.tier}</strong>
          </p>
        )}
      </div>

      <div className="mt-4 pt-3 border-t border-border flex items-center justify-between gap-2">
        <button onClick={onReset}
          data-testid="anticoag-screening-new"
          className="text-sm font-semibold inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-border">
          <RefreshCw className="w-3.5 h-3.5" /> New screening
        </button>
        <button onClick={onOpenFull}
          data-testid="anticoag-screening-open-full"
          className="text-xs font-semibold underline-offset-2 hover:underline"
          style={{ color: C.color }}>
          Open full framework →
        </button>
      </div>
    </StepCard>
  );
}

// ─────────────────────────── shared bits ─────────────────────────────────
function StepCard({ stepNum, heading, script, children, testid }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-5 space-y-4" data-testid={testid}>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: C.color }}>
          Step {stepNum} of 5
        </p>
        <h2 className="text-lg sm:text-xl font-bold text-foreground mt-1">{heading}</h2>
        {script && (
          <p className="text-[11px] text-muted-foreground mt-2 italic"
             data-testid={`${testid}-script`}>
            <MessageCircle className="w-3 h-3 inline mr-1" /> {script}
          </p>
        )}
      </div>
      <div>{children}</div>
    </div>
  );
}

function BigYN({ active, onClick, children, testid, warn }) {
  const color = warn ? '#DC2626' : C.color;
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="flex-1 text-base font-bold py-3 rounded-lg border-2 transition-colors"
      style={active
        ? { backgroundColor: `${color}15`, borderColor: color, color }
        : { borderColor: 'hsl(var(--border))', color: 'hsl(var(--foreground))' }}>
      {children}
    </button>
  );
}

function RiskChip({ risk }) {
  const colors = { low: ['#16A34A15', '#15803D'], moderate: ['#D9770615', '#B45309'], high: ['#DC262615', '#B91C1C'] };
  const [bg, fg] = colors[risk] || colors.low;
  return (
    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
          style={{ backgroundColor: bg, color: fg }}>{risk} bleeding risk</span>
  );
}
